import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import Layout from "../../component/layout";
import { toastify } from "../../../../utils/toast";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { useFacilityStore } from "../../../../app/z-store/store";
import { addQuotationRequestsUrl ,  getPurchaseRequestsUrl , getSuppliersUrl } from "../../../../utils/urls";

const AddRequestForQuotation = () => {
  const navigate = useNavigate();
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);
  const toast = useRef(null);

  // Form fields
  const [formData, setFormData] = useState({
    name: "",
    startDate: null,
    closingDate: null,
    currency: "KES",
    rfqType: "closed",
    rfqFee: 0,
    rfqEvaluationType: "automatic",
    notes: "",
    category: "",
    suppliers: [],
    items: []
  });

  // Suppliers state
  const [supplierDialogVisible, setSupplierDialogVisible] = useState(false);
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [supplierFilter, setSupplierFilter] = useState("");

  // Purchase request state
  const [purchaseRequests, setPurchaseRequests] = useState([]);
  const [selectedPurchaseRequest, setSelectedPurchaseRequest] = useState(null);
  const [purchaseRequestLoading, setPurchaseRequestLoading] = useState(false);

  // Loading & submission state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Options for dropdowns
  const currencyOptions = [
    { label: "KES", value: "KES" },
    { label: "USD", value: "USD" },
    { label: "EUR", value: "EUR" },
    { label: "GBP", value: "GBP" },
  ];

  const rfqTypeOptions = [
    { label: "Open", value: "open" },
    { label: "Closed", value: "closed" },
  ];

  const categoryOptions = [
    { label: "Service", value: "Service" },
    { label: "Product", value: "Product" },
    { label: "Maintenance", value: "Maintenance" },
    { label: "Construction", value: "Construction" },
  ];

  const unitOfMeasureOptions = [
    { label: "Pieces", value: "Pieces" },
    { label: "Pcs", value: "Pcs" },
    { label: "Litres", value: "Litres" },
    { label: "Box", value: "Box" },
    { label: "Kg", value: "Kg" },
    { label: "Hours", value: "Hours" },
    { label: "Days", value: "Days" },
    { label: "Each", value: "Each" },
  ];

  // Handle form field changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Fetch suppliers and purchase requests when component mounts
  useEffect(() => {
    if (!selectedFacilityId) return;

    fetchSuppliers();
    fetchPurchaseRequests();
  }, [selectedFacilityId]);

  const fetchSuppliers = async () => {
    try {
      setSupplierLoading(true);
      const response = await makeRequest2(
        `${getSuppliersUrl}/${selectedFacilityId}`,
        "GET"
      );

      if (response.success && response.data && Array.isArray(response.data.data)) {
        // Map the response and include the userId field
        setAvailableSuppliers(response.data.data.map(supplier => ({
          id: supplier._id,
          name: supplier.name,
          contactPerson: supplier.contactPerson?.name || 'N/A',
          email: supplier.email || 'N/A',
          phone: supplier.phone || 'N/A',
          userId: supplier.userId || null // Include the userId field
        })));
      } else {
        toastify("Failed to load suppliers", "error");
      }
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      toastify("Error fetching suppliers", "error");
    } finally {
      setSupplierLoading(false);
    }
  };

  // Fetch purchase requests
  const fetchPurchaseRequests = async () => {
    try {
      setPurchaseRequestLoading(true);
      const response = await makeRequest2(
       `${getPurchaseRequestsUrl}/${selectedFacilityId}`,
        "GET"
      );

      if (response.success && response.data && Array.isArray(response.data.data)) {
        // Filter to only include approved purchase requests
        const approvedRequests = response.data.data
          .filter(pr => (pr.status || '').toLowerCase() === 'approved')
          .map(pr => ({
            id: pr._id,
            irfNumber: pr.irfNumber,
            department: pr.department || 'N/A',
            date: new Date(pr.date).toLocaleDateString(),
            from: pr.from || 'N/A',
            items: pr.items || []
          }));
        
        setPurchaseRequests(approvedRequests);
      } else {
        setPurchaseRequests([]);
      }
    } catch (error) {
      console.error("Error fetching purchase requests:", error);
      setPurchaseRequests([]);
    } finally {
      setPurchaseRequestLoading(false);
    }
  };

  // Handle purchase request selection
  const handlePurchaseRequestChange = (value) => {
    setSelectedPurchaseRequest(value);
    
    if (value) {
      // Map the purchase request items to RFQ item format
      const mappedItems = value.items.map(item => ({
        itemName: item.itemDescription || '',
        description: item.remarksSpecification || '',
        quantity: item.quantity || 1,
        unitOfMeasure: item.unitOfMeasure || '',
        specifications: item.remarksSpecification || '',
      }));

      // Prefill the items in the form
      setFormData(prev => ({
        ...prev,
        items: mappedItems,
        // Optionally set more fields based on the purchase request
        name: `RFQ for ${value.irfNumber}`,
        notes: `Based on purchase request ${value.irfNumber} from ${value.department}`,
      }));
    }
  };

  // Filter suppliers in the dialog
  const filteredSuppliers = supplierFilter
    ? availableSuppliers.filter(supplier =>
      supplier.name.toLowerCase().includes(supplierFilter.toLowerCase()) ||
      (supplier.email && supplier.email.toLowerCase().includes(supplierFilter.toLowerCase())) ||
      (supplier.contactPerson && supplier.contactPerson.toLowerCase().includes(supplierFilter.toLowerCase()))
    )
    : availableSuppliers;

  // If no facility is selected, display a message and exit early.
  if (!selectedFacilityId) {
    return (
      <Layout>
        <div className="card">
          <div className="card-body text-center">
            <h5>Please select a facility first.</h5>
            <Button
              label="Go to Facilities"
              className="p-button-primary mt-3"
              onClick={() => navigate("/facility")}
            />
          </div>
        </div>
      </Layout>
    );
  }

  // Open supplier selection dialog
  const openSupplierDialog = () => {
    setSupplierDialogVisible(true);
    setSelectedSuppliers([]);
  };

  // Add selected suppliers to the RFQ
  const addSelectedSuppliers = () => {
    if (!selectedSuppliers || selectedSuppliers.length === 0) {
      toastify("No suppliers selected", "info");
      setSupplierDialogVisible(false);
      return;
    }

    // Filter out suppliers that are already added
    const newSuppliers = selectedSuppliers.filter(
      selected => !formData.suppliers.some(s => s.id === selected.id)
    );

    if (newSuppliers.length === 0) {
      toastify("Selected suppliers are already added", "info");
      setSupplierDialogVisible(false);
      return;
    }

    setFormData(prev => ({
      ...prev,
      suppliers: [...prev.suppliers, ...newSuppliers]
    }));

    setSupplierDialogVisible(false);
    toastify(`${newSuppliers.length} supplier(s) added successfully`, "success");
  };

  // Remove a supplier
  const handleRemoveSupplier = (supplierId) => {
    setFormData(prev => ({
      ...prev,
      suppliers: prev.suppliers.filter(s => s.id !== supplierId)
    }));
  };

  // Add a new row for an item
  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          itemName: "",
          description: "",
          quantity: 1,
          unitOfMeasure: "",
          specifications: "",
        }
      ]
    }));
  };

  // Handle changes for each item field
  const handleItemChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // Remove an item row
  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Clear the form
  const handleClear = () => {
    setFormData({
      name: "",
      startDate: null,
      closingDate: null,
      currency: "KES",
      rfqType: "closed",
      rfqFee: 0,
      rfqEvaluationType: "automatic",
      notes: "",
      category: "",
      suppliers: [],
      items: []
    });
    setSelectedPurchaseRequest(null);
  };

  // Validate form before submission
  const validateForm = () => {
    if (!formData.name) {
      toastify("Please enter a name for this RFQ", "error");
      return false;
    }

    if (!formData.startDate || !formData.closingDate) {
      toastify("Please select start and closing dates", "error");
      return false;
    }

    if (new Date(formData.startDate) >= new Date(formData.closingDate)) {
      toastify("Closing date must be after start date", "error");
      return false;
    }

    if (!formData.currency) {
      toastify("Please select a currency", "error");
      return false;
    }

    if (!formData.rfqType) {
      toastify("Please select an RFQ type", "error");
      return false;
    }

    if (!formData.category) {
      toastify("Please select a category", "error");
      return false;
    }

    if (formData.items.length === 0) {
      toastify("Please add at least one item", "error");
      return false;
    }

    // Validate items
    for (let i = 0; i < formData.items.length; i++) {
      const item = formData.items[i];
      if (!item.itemName) {
        toastify(`Please enter a name for item #${i + 1}`, "error");
        return false;
      }
      if (!item.quantity || item.quantity <= 0) {
        toastify(`Please enter a valid quantity for item #${i + 1}`, "error");
        return false;
      }
      if (!item.unitOfMeasure) {
        toastify(`Please select a unit of measure for item #${i + 1}`, "error");
        return false;
      }
    }

    return true;
  };

  // Submit the RFQ
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Use userId as the supplier identifier if available, otherwise use the supplier's _id
      const payload = {
        facilityId: selectedFacilityId,
        name: formData.name,
        startDate: formData.startDate,
        closingDate: formData.closingDate,
        currency: formData.currency,
        rfqType: formData.rfqType,
        rfqFee: formData.rfqFee,
        rfqEvaluationType: formData.rfqEvaluationType,
        notes: formData.notes,
        category: formData.category,
        suppliers: formData.suppliers.map(s => s.userId || s.id), // Use userId if available, fall back to id
        items: formData.items.map(item => ({
          itemName: item.itemName,
          description: item.description,
          quantity: item.quantity,
          unitOfMeasure: item.unitOfMeasure,
          specifications: item.specifications,
        })),
        // If a purchase request was selected, include its ID
        purchaseRequestId: selectedPurchaseRequest ? selectedPurchaseRequest.id : undefined
      };

      const response = await makeRequest2(
        `${addQuotationRequestsUrl}/${selectedFacilityId}`,
        "POST",
        payload
      );

      if (!response.success) {
        toastify(response.error || "Failed to create RFQ", "error");
        return;
      }

      toastify(response.message || "Request for Quotation created successfully", "success");
      handleClear();
      navigate("/facility/procurement_management/quotations_management");
    } catch (error) {
      toastify("An unexpected error occurred: " + (error.message || "Unknown error"), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Footer for supplier dialog
  const supplierDialogFooter = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => setSupplierDialogVisible(false)}
      />
      <Button
        label="Add Selected"
        icon="pi pi-check"
        className="p-button-primary"
        onClick={addSelectedSuppliers}
        disabled={selectedSuppliers.length === 0}
      />
    </div>
  );

  return (
    <Layout>
      {/* Page Header & Breadcrumbs */}
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/facility/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/facility/procurement_management/quotations_management">
                    Request For Quotations
                  </Link>
                </li>
                <li className="breadcrumb-item">New Request For Quotation</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main Form */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <Link to="/facility/procurement_management/quotations_management">
                <i className="ti ti-arrow-narrow-left"></i> Back
              </Link>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h5>New Request For Quotation</h5>
            </div>
            <div className="card-body">
              {/* Purchase Request Selection */}
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">Select Purchase Request (Optional)</h5>
                </div>
                <div className="card-body">
                  <div className="alert alert-info mb-3">
                    <i className="ti ti-info-circle me-2"></i>
                    Selecting an approved purchase request will pre-fill the items section.
                  </div>
                  
                  <Dropdown
                    value={selectedPurchaseRequest}
                    options={purchaseRequests}
                    onChange={(e) => handlePurchaseRequestChange(e.value)}
                    optionLabel="irfNumber"
                    filter
                    filterBy="irfNumber,department"
                    placeholder="Select an approved purchase request"
                    className="w-100"
                    disabled={isSubmitting || purchaseRequestLoading}
                    loading={purchaseRequestLoading}
                    emptyMessage={purchaseRequestLoading ? "Loading purchase requests..." : "No approved purchase requests found"}
                    itemTemplate={(option) => (
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <span className="font-weight-bold">{option.irfNumber}</span>
                          <span className="ms-2 text-muted">{option.department}</span>
                        </div>
                        <div className="text-end text-muted small">
                          {option.date} | {option.items.length} items
                        </div>
                      </div>
                    )}
                  />
                </div>
              </div>

              {/* Basic Information */}
              <div className="row mb-4">
                <div className="col-md-4">
                  <label className="form-label">Name <span className="text-danger">*</span></label>
                  <InputText
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    className="w-100"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Start Date <span className="text-danger">*</span></label>
                  <Calendar
                    value={formData.startDate}
                    onChange={(e) => handleInputChange("startDate", e.value)}
                    showIcon
                    className="w-100"
                    minDate={new Date()}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Closing Date <span className="text-danger">*</span></label>
                  <Calendar
                    value={formData.closingDate}
                    onChange={(e) => handleInputChange("closingDate", e.value)}
                    showIcon
                    className="w-100"
                    minDate={formData.startDate || new Date()}
                    disabled={isSubmitting || !formData.startDate}
                  />
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-md-4">
                  <label className="form-label">Currency <span className="text-danger">*</span></label>
                  <Dropdown
                    value={formData.currency}
                    options={currencyOptions}
                    onChange={(e) => handleInputChange("currency", e.value)}
                    placeholder="Select Currency"
                    className="w-100"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">RFQ Type <span className="text-danger">*</span></label>
                  <Dropdown
                    value={formData.rfqType}
                    options={rfqTypeOptions}
                    onChange={(e) => handleInputChange("rfqType", e.value)}
                    placeholder="Select Type"
                    className="w-100"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Category <span className="text-danger">*</span></label>
                  <Dropdown
                    value={formData.category}
                    options={categoryOptions}
                    onChange={(e) => handleInputChange("category", e.value)}
                    placeholder="Select Category"
                    className="w-100"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-md-4">
                  <label className="form-label">RFQ Fee</label>
                  <InputNumber
                    value={formData.rfqFee}
                    onValueChange={(e) => handleInputChange("rfqFee", e.value)}
                    mode="currency"
                    currency={formData.currency}
                    locale="en-US"
                    className="w-100"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="col-md-8">
                  <label className="form-label">RFQ Evaluation Type <span className="text-danger">*</span></label>
                  <div className="d-flex gap-4 mt-2">
                    <div className="form-check">
                      <input
                        type="radio"
                        id="automatic"
                        name="rfqEvaluationType"
                        className="form-check-input"
                        checked={formData.rfqEvaluationType === "automatic"}
                        onChange={() => handleInputChange("rfqEvaluationType", "automatic")}
                        disabled={isSubmitting}
                      />
                      <label className="form-check-label" htmlFor="automatic">
                        Automatic (Cost-based)
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        id="approvers"
                        name="rfqEvaluationType"
                        className="form-check-input"
                        checked={formData.rfqEvaluationType === "approvers"}
                        onChange={() => handleInputChange("rfqEvaluationType", "approvers")}
                        disabled={isSubmitting}
                      />
                      <label className="form-check-label" htmlFor="approvers">
                        Through Approvers
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="form-label">Notes</label>
                <InputTextarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  rows={3}
                  className="w-100"
                  disabled={isSubmitting}
                />
              </div>

              {/* Suppliers Section */}
              <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Suppliers</h5>
                  <Button
                    label="Add Suppliers"
                    icon="pi pi-plus"
                    className="p-button-primary"
                    onClick={openSupplierDialog}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="card-body">
                  {formData.suppliers.length === 0 ? (
                    <div className="alert alert-info text-center">
                      No suppliers added yet. Click "Add Suppliers" to select suppliers for this RFQ.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-bordered table-striped">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Supplier Name</th>
                            <th>Contact Person</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.suppliers.map((supplier, index) => (
                            <tr key={supplier.id}>
                              <td>{index + 1}</td>
                              <td>{supplier.name}</td>
                              <td>{supplier.contactPerson || "N/A"}</td>
                              <td>{supplier.email || "N/A"}</td>
                              <td>{supplier.phone || "N/A"}</td>
                              <td>
                                <Button
                                  icon="ti ti-trash"
                                  className="p-button-rounded p-button-danger"
                                  onClick={() => handleRemoveSupplier(supplier.id)}
                                  disabled={isSubmitting}
                                  tooltip="Remove"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Items Section */}
              <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Items <span className="text-danger">*</span></h5>
                  <div>
                    {selectedPurchaseRequest && (
                      <span className="badge bg-info me-2">
                        Using items from PR: {selectedPurchaseRequest.irfNumber}
                      </span>
                    )}
                    <Button
                      label="Add Item"
                      icon="pi pi-plus"
                      className="p-button-primary"
                      onClick={handleAddItem}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                <div className="card-body">
                  {formData.items.length === 0 ? (
                    <div className="alert alert-info text-center">
                      No items added yet. Click "Add Item" to add items manually or select a purchase request above to import items.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-bordered table-striped">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Item Name <span className="text-danger">*</span></th>
                            <th>Description</th>
                            <th>Quantity <span className="text-danger">*</span></th>
                            <th>Unit of Measure <span className="text-danger">*</span></th>
                            <th>Specifications</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.items.map((item, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>
                                <InputText
                                  value={item.itemName}
                                  onChange={(e) => handleItemChange(index, "itemName", e.target.value)}
                                  className="w-100"
                                  disabled={isSubmitting}
                                />
                              </td>
                              <td>
                                <InputText
                                  value={item.description}
                                  onChange={(e) => handleItemChange(index, "description", e.target.value)}
                                  className="w-100"
                                  disabled={isSubmitting}
                                />
                              </td>
                              <td>
                                <InputNumber
                                  value={item.quantity}
                                  onValueChange={(e) => handleItemChange(index, "quantity", e.value)}
                                  min={1}
                                  className="w-100"
                                  disabled={isSubmitting}
                                />
                              </td>
                              <td>
                                <Dropdown
                                  value={item.unitOfMeasure}
                                  options={unitOfMeasureOptions}
                                  onChange={(e) => handleItemChange(index, "unitOfMeasure", e.value)}
                                  placeholder="Select Unit"
                                  className="w-100"
                                  disabled={isSubmitting}
                                />
                              </td>
                              <td>
                                <InputText
                                  value={item.specifications}
                                  onChange={(e) => handleItemChange(index, "specifications", e.target.value)}
                                  className="w-100"
                                  disabled={isSubmitting}
                                />
                              </td>
                              <td>
                                <Button
                                  icon="ti ti-trash"
                                  className="p-button-rounded p-button-danger"
                                  onClick={() => handleRemoveItem(index)}
                                  disabled={isSubmitting}
                                  tooltip="Remove"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="text-end mt-4">
                <Button
                  type="button"
                  label="Clear"
                  icon="pi pi-times"
                  className="p-button-outlined p-button-secondary me-2"
                  onClick={handleClear}
                  disabled={isSubmitting}
                />
                <Button
                  type="button"
                  label={isSubmitting ? "Submitting..." : "Submit"}
                  icon="pi pi-check"
                  className="p-button-primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Supplier Selection Dialog */}
      <Dialog
        header="Select Suppliers"
        visible={supplierDialogVisible}
        style={{ width: '80vw' }}
        modal
        onHide={() => setSupplierDialogVisible(false)}
        footer={supplierDialogFooter}
      >
        <div className="mb-3">
          <InputText
            placeholder="Search suppliers..."
            className="w-100"
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
          />
        </div>

        <DataTable
          value={filteredSuppliers}
          selection={selectedSuppliers}
          onSelectionChange={(e) => setSelectedSuppliers(e.value)}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25]}
          dataKey="id"
          loading={supplierLoading}
          emptyMessage="No suppliers found"
          selectionMode="multiple"
          stripedRows
        >
          <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
          <Column field="name" header="Supplier Name" sortable />
          <Column field="contactPerson" header="Contact Person" sortable />
          <Column field="email" header="Email" sortable />
          <Column field="phone" header="Phone" sortable />
        </DataTable>
      </Dialog>
    </Layout>
  );
};

export default AddRequestForQuotation;